/**
 * ScopeFilter — the connector library's one organization filter (item 83,
 * Segev 2026-11-02). It replaces the "Managing" select and item 74's
 * one-organization filter: the organizations the page shows, chosen from the
 * tree of everything this admin manages, three ways side by side —
 *  - a click on a name: only that organization;
 *  - boxes: several;
 *  - "+ all under it": an organization and its whole branch (organizations
 *    added there later too).
 * Choices are a draft until Done (or, on a desktop, a click outside); Esc
 * and Cancel leave the page as it was. On a phone it opens as a sheet from
 * the bottom, whose close is Cancel. At most MAX_PICKS picks — a branch
 * covers any number.
 *
 * The rules are utils/scope.ts; the tree, search and opening are the
 * Organizations tab's (utils/orgTree.ts) in its own rows' look.
 */

import { useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { Checkbox, Drawer, Popover } from 'antd';
import { useLanguage } from '@hooks/useLanguage';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { mirrorPlacement } from '@/utils/rtl';
import { bidiIsolate } from '@/utils/bidi';
import { buildTree, flatten, search, shownNodes, type TreeOrg } from '../utils/orgTree';
import {
  covered, defaultPicks, MAX_PICKS, only, pickWords, scopeIds, scopeWords, toggle, toggleBelow, type Pick,
} from '../utils/scope';
import './LibraryOrganizations.css';

interface Props {
  /** Every organization the admin manages, each with its parent. */
  orgs: TreeOrg[];
  value: Pick[];
  onChange: (picks: Pick[]) => void;
  /** The admin's own organization, for "Only <it>". */
  ownId: string | null;
}

export function ScopeFilter({ orgs, value, onChange, ownId }: Props) {
  const { t, direction } = useLanguage();
  const phone = useMediaQuery('(max-width: 768px)');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Pick[]>(value);
  const [query, setQuery] = useState('');
  // Closed branches. A deep tree starts with its first two levels open.
  const [closed, setClosed] = useState<Set<string> | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);

  const tree = useMemo(() => buildTree(orgs), [orgs]);
  const rows = useMemo(() => flatten(tree), [tree]);
  const found = useMemo(() => search(tree, query), [tree, query]);
  const shut = closed ?? new Set(rows.filter(n => n.depth >= 1 && n.children.length > 0).map(n => n.org.tenant_id));
  const shown = shownNodes(rows, found, id => !shut.has(id));
  const inside = useMemo(() => covered(orgs, draft), [orgs, draft]);
  const own = orgs.find(o => o.tenant_id === ownId) ?? null;
  const everything = useMemo(() => defaultPicks(orgs, null), [orgs]);

  const begin = () => { setDraft(value); setQuery(''); setOpen(true); };
  const end = (apply: boolean) => {
    if (apply && draft.length > 0 && draft.length <= MAX_PICKS) onChange(draft);
    setOpen(false);
    setQuery('');
    button.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.stopPropagation(); end(false); return; }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const stops = [...(list.current?.querySelectorAll<HTMLButtonElement>('.cxl-scopef__name') ?? [])];
    if (stops.length === 0) return;
    e.preventDefault();
    const at = stops.indexOf(document.activeElement as HTMLButtonElement);
    const next = e.key === 'ArrowDown' ? Math.min(at + 1, stops.length - 1) : at - 1;
    if (next < 0) list.current?.parentElement?.querySelector<HTMLInputElement>('input')?.focus();
    else stops[next].focus();
  };

  const words = pickWords(orgs, draft);
  const total = scopeIds(orgs, draft).size;

  const panel = (
    <div className="cxl-scopef" onKeyDown={onKeyDown} data-testid="connector-library-scope-panel">
      <input className="form-input cxl-scopef__search" type="search" value={query} dir="auto" autoFocus={!phone}
             onChange={e => setQuery(e.target.value)} placeholder={t('connectorLibrary.orgFilter.search')}
             aria-label={t('connectorLibrary.orgFilter.search')} data-testid="connector-library-scope-search" />
      <div className="cxl-scopef__quick">
        <button type="button" className="cxl-scopef__chip" onClick={() => setDraft(everything)}
                data-testid="connector-library-scope-all">{t('connectorLibrary.scope.all')}</button>
        {own && (
          <button type="button" className="cxl-scopef__chip" onClick={() => setDraft(only(own.tenant_id))}
                  data-testid="connector-library-scope-own">
            {t('connectorLibrary.scope.only')} <bdi dir="auto">{own.name}</bdi>
          </button>
        )}
        <button type="button" className="cxl-row__link cxl-scopef__clear" onClick={() => setDraft([])}
                data-testid="connector-library-scope-clear">{t('connectorLibrary.scope.clear')}</button>
      </div>
      <ul className="cxl-orgs__tree cxl-scopef__tree" role="tree" ref={list} aria-multiselectable="true"
          aria-label={t('connectorLibrary.orgFilter.treeLabel')}>
        {shown.map(node => {
          const id = node.org.tenant_id;
          const hasKids = node.children.length > 0;
          const isOpen = !shut.has(id) || found.open.has(id);
          const pick = draft.find(p => p.id === id);
          const isCovered = inside.has(id);
          const under = node.ids.length - 1;
          return (
            <li key={id} className={`cxl-orgs__row cxl-scopef__row ${found.matches.has(id) ? 'is-match' : ''} ${pick ? 'is-on' : ''}`}
                role="treeitem" aria-level={node.depth + 1} aria-expanded={hasKids ? isOpen : undefined}
                aria-selected={!!pick || isCovered} style={{ '--depth': node.depth } as CSSProperties}
                data-testid={`connector-library-scope-row-${id}`}>
              <span className="cxl-orgs__caret-slot">
                {hasKids && (
                  <button type="button" className={`cxl-orgs__caret ${isOpen ? 'is-open' : ''}`} tabIndex={-1}
                          onClick={() => setClosed(() => {
                            const next = new Set(shut);
                            if (next.has(id)) next.delete(id); else next.add(id);
                            return next;
                          })}
                          aria-label={t(isOpen ? 'connectorLibrary.orgs.collapse' : 'connectorLibrary.orgs.expand',
                                        { name: node.org.name })}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
                  </button>
                )}
              </span>
              <Checkbox checked={!!pick || isCovered} disabled={isCovered} onChange={() => setDraft(d => toggle(d, id))}
                        aria-label={t('connectorLibrary.scope.tick', { name: node.org.name })}
                        data-testid={`connector-library-scope-check-${id}`} />
              <button type="button" className="cxl-scopef__name" onClick={() => setDraft(only(id))}
                      title={t('connectorLibrary.scope.onlyThis', { name: node.org.name })}
                      data-testid={`connector-library-scope-only-${id}`}>
                <bdi dir="auto" className="cxl-orgs__name">{node.org.name}</bdi>
                {isCovered
                  ? <span className="cxl-scopef__note">{t('connectorLibrary.scope.included')}</span>
                  : under > 0 && <span className="cxl-scopef__note">{t('connectorLibrary.scope.underCount', { count: under })}</span>}
              </button>
              <span className="cxl-scopef__end">
                {hasKids && !isCovered && (
                  <button type="button" className={`cxl-scopef__below ${pick?.below ? 'is-on' : ''}`}
                          aria-pressed={!!pick?.below} onClick={() => setDraft(d => toggleBelow(orgs, d, id))}
                          data-testid={`connector-library-scope-below-${id}`}>
                    {pick?.below && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
                           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>
                    )}
                    {t('connectorLibrary.scope.allUnder')}
                  </button>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      {query.trim() && found.visible.size === 0 && (
        <p className="cxl-panel__empty" role="status">{t('connectorLibrary.orgFilter.noMatch')}</p>
      )}
      <p className="cxl-scopef__summary" role="status" data-testid="connector-library-scope-summary">
        {draft.length === 0 ? t('connectorLibrary.scope.chooseOne')
          : draft.length > MAX_PICKS ? t('connectorLibrary.scope.tooMany', { count: MAX_PICKS }) : (
          <>
            <span className="cxl-scopef__summary-label">{t('connectorLibrary.scope.showingLabel')}</span>{' '}
            {scopeWords(words, t)}
            <span className="cxl-scopef__summary-count"> · {t('connectorLibrary.scope.orgCount', { count: total })}</span>
          </>
        )}
      </p>
      <p className="cxl-scopef__help">{t('connectorLibrary.scope.help')}</p>
      <div className="cxl-scopef__foot">
        <button type="button" className="cx-btn" onClick={() => end(false)}>{t('common.cancel')}</button>
        <button type="button" className="cx-btn cx-btn--primary" disabled={draft.length === 0 || draft.length > MAX_PICKS} onClick={() => end(true)}
                data-testid="connector-library-scope-done">{t('connectorLibrary.scope.done')}</button>
      </div>
    </div>
  );

  const shownWords = pickWords(orgs, value);
  const first = shownWords[0];
  const label = !first ? t('connectorLibrary.scope.none')
    : shownWords.length > 1
      ? t('connectorLibrary.scope.buttonSeveral', { name: bidiIsolate(first.name), count: shownWords.length - 1 })
      : first.below && first.count > 0
        ? t('connectorLibrary.scope.buttonBranch', { name: bidiIsolate(first.name), count: first.count })
        : first.name;

  const trigger = (
    <button type="button" ref={button} className="cxl-orgfilter__button is-on" aria-haspopup="dialog" aria-expanded={open}
            onClick={phone ? begin : undefined} title={t('connectorLibrary.scope.hint')}
            data-testid="connector-library-scope">
      <span className="cxl-orgfilter__label">{t('connectorLibrary.scope.label')}</span>
      <bdi dir="auto" className="cxl-orgfilter__value">{label}</bdi>
      <svg className="cxl-orgfilter__chevron" width="10" height="10" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 9l6 6 6-6" />
      </svg>
    </button>
  );

  if (phone) {
    return (
      <>
        {trigger}
        <Drawer open={open} placement="bottom" onClose={() => end(false)} title={t('connectorLibrary.scope.label')}
                height="85vh" className="cxl-scopef-sheet" destroyOnHidden>
          {panel}
        </Drawer>
      </>
    );
  }
  return (
    <Popover content={panel} trigger={['click']} open={open} arrow={false}
             onOpenChange={next => { if (next) begin(); else end(true); }}
             placement={mirrorPlacement(direction, 'bottomLeft')} overlayClassName="cxl-orgfilter-pop" destroyOnHidden>
      {trigger}
    </Popover>
  );
}
